import { navigationConfig } from "../../config/navigationConfig";
import { NavigationItem } from "../../types/navigation";

/**
 * Admin home (command center) pure config + helpers.
 *
 * Single source of truth for the tile board and search index is
 * `navigationConfig.adminMenuItems`. The `home` item is excluded everywhere
 * here (you do not tile or search for the page you are already on).
 *
 * No DOM, no fetch -- fully synchronous and unit-testable.
 */

/** Swimlane render order on the admin home board. */
export const ADMIN_GROUP_ORDER: string[] = [
  "Users & Accounts",
  "Products & Content",
  "Marketing Site",
  "Trading & Rebalancing",
  "Verification & Risk",
  "Operations & Reporting",
  "Configuration",
];

/** Trailing lane for any destination whose group is missing/unknown (defensive). */
export const FALLBACK_GROUP = "More";

export interface GroupStyle {
  /** Lane title + count text. */
  text: string;
  /** Lane header dot. */
  dot: string;
  /** Tile icon chip. */
  chip: string;
  /** Tile left spine. */
  spine: string;
  /** Tile hover border + shadow. */
  hover: string;
}

/**
 * Per-category Tailwind class strings (earthy/sober `rp-cat-*` tokens).
 * Written as full literal strings so Tailwind's content scanner keeps them.
 */
export const GROUP_STYLES: Record<string, GroupStyle> = {
  "Users & Accounts": { text: "text-rp-cat-users", dot: "bg-rp-cat-users", chip: "bg-rp-cat-users/10 text-rp-cat-users", spine: "bg-rp-cat-users", hover: "hover:border-rp-cat-users/60 hover:shadow-rp-cat-users/20" },
  "Products & Content": { text: "text-rp-cat-products", dot: "bg-rp-cat-products", chip: "bg-rp-cat-products/10 text-rp-cat-products", spine: "bg-rp-cat-products", hover: "hover:border-rp-cat-products/60 hover:shadow-rp-cat-products/20" },
  "Marketing Site": { text: "text-rp-cat-marketing", dot: "bg-rp-cat-marketing", chip: "bg-rp-cat-marketing/10 text-rp-cat-marketing", spine: "bg-rp-cat-marketing", hover: "hover:border-rp-cat-marketing/60 hover:shadow-rp-cat-marketing/20" },
  "Trading & Rebalancing": { text: "text-rp-cat-trading", dot: "bg-rp-cat-trading", chip: "bg-rp-cat-trading/10 text-rp-cat-trading", spine: "bg-rp-cat-trading", hover: "hover:border-rp-cat-trading/60 hover:shadow-rp-cat-trading/20" },
  "Verification & Risk": { text: "text-rp-cat-risk", dot: "bg-rp-cat-risk", chip: "bg-rp-cat-risk/10 text-rp-cat-risk", spine: "bg-rp-cat-risk", hover: "hover:border-rp-cat-risk/60 hover:shadow-rp-cat-risk/20" },
  "Operations & Reporting": { text: "text-rp-cat-ops", dot: "bg-rp-cat-ops", chip: "bg-rp-cat-ops/10 text-rp-cat-ops", spine: "bg-rp-cat-ops", hover: "hover:border-rp-cat-ops/60 hover:shadow-rp-cat-ops/20" },
  "Configuration": { text: "text-rp-cat-config", dot: "bg-rp-cat-config", chip: "bg-rp-cat-config/10 text-rp-cat-config", spine: "bg-rp-cat-config", hover: "hover:border-rp-cat-config/60 hover:shadow-rp-cat-config/20" },
  [FALLBACK_GROUP]: { text: "text-gray-400", dot: "bg-gray-500", chip: "bg-gray-500/10 text-gray-400", spine: "bg-gray-500", hover: "hover:border-gray-500/60 hover:shadow-gray-500/20" },
};

export function getGroupStyle(group: string): GroupStyle {
  return GROUP_STYLES[group] ?? GROUP_STYLES[FALLBACK_GROUP];
}

/** All admin destinations shown on the home (every admin nav item except `home`). */
export function getAdminDestinations(): NavigationItem[] {
  return navigationConfig.adminMenuItems.filter(item => item.id !== "home");
}

/**
 * Searchable index for the home search bar + command palette: every
 * destination plus one entry per sub-page of a collapsible item (PWA-2748).
 * Children inherit the parent's icon, group and feature flag so the display
 * boundaries filter them together; their label is prefixed with the parent's.
 */
export function buildAdminHomeIndex(): NavigationItem[] {
  return getAdminDestinations().flatMap(item => [
    item,
    ...(item.children ?? []).map<NavigationItem>(child => ({
      id: child.id,
      label: `${item.label} · ${child.label}`,
      icon: item.icon,
      path: child.path,
      group: item.group,
      description: child.description ?? item.description,
      keywords: [...(item.keywords ?? []), ...(child.keywords ?? [])],
    })),
  ]);
}

export interface AdminHomeLane {
  group: string;
  items: NavigationItem[];
}

/** Group destinations into ordered swimlanes; unknown groups fall into a trailing "More" lane. */
export function groupAdminDestinations(items: NavigationItem[]): AdminHomeLane[] {
  const byGroup = new Map<string, NavigationItem[]>();
  ADMIN_GROUP_ORDER.forEach(group => byGroup.set(group, []));
  const more: NavigationItem[] = [];

  items.forEach(item => {
    const bucket = item.group ? byGroup.get(item.group) : undefined;
    if (bucket) {
      bucket.push(item);
    } else {
      more.push(item);
    }
  });

  const lanes: AdminHomeLane[] = ADMIN_GROUP_ORDER
    .map(group => ({ group, items: byGroup.get(group) ?? [] }))
    .filter(lane => lane.items.length > 0);

  if (more.length > 0) {
    lanes.push({ group: FALLBACK_GROUP, items: more });
  }
  return lanes;
}

/** Lowercased haystack for matching: label + group + keywords + description. */
export function adminItemHaystack(item: NavigationItem): string {
  return [item.label, item.group ?? "", (item.keywords ?? []).join(" "), item.description ?? ""]
    .join(" ")
    .toLowerCase();
}

/** True when the item matches the query (used for inline board filtering). */
export function matchesAdminQuery(item: NavigationItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return adminItemHaystack(item).includes(q);
}

/**
 * Rank destinations against a query.
 * label-prefix (3) > label-substring (2) > keyword/group/description (1).
 * Empty query returns every item in source order. Sort is stable, so ties
 * preserve config order.
 */
export function searchAdminHome(items: NavigationItem[], query: string): NavigationItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...items];

  const scored: Array<{ item: NavigationItem; score: number }> = [];
  items.forEach(item => {
    const name = item.label.toLowerCase();
    let score = -1;
    if (name.startsWith(q)) {
      score = 3;
    } else if (name.includes(q)) {
      score = 2;
    } else if (adminItemHaystack(item).includes(q)) {
      score = 1;
    }
    if (score >= 0) {
      scored.push({ item, score });
    }
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.map(entry => entry.item);
}
